import {
	Currency,
	PelContractorType,
	PelRegion,
	pelContractorTypeSchema,
	pelRegionSchema,
} from "@inrev/common";
import { useCalculatePelPml } from "@portal/api/pel-pml/admin";
import { FormCurrencyComponent, FormDropdownComponent } from "@portal/components/form/fieldLayouts";
import { createFormResolver } from "@portal/components/multiform/utils/formResolver";
import { EmptyState } from "@portal/components/patterns";
import { Banner, Card, Icon, Spinner, Text } from "@portal/components/primitives";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { z } from "zod";
import { PelPmlBreakdown } from "../shared/PelPmlBreakdown";

type PelPmlFormValues = {
	totalExposure: number | null;
	bondAmount: number | null;
	contractorType: PelContractorType | null;
	region: PelRegion | null;
};

// Missing fields stay quiet while typing; only the cross-field server constraint is surfaced.
const formSchema = z
	.object({
		totalExposure: z.number().positive("Must be greater than 0").nullable(),
		bondAmount: z.number().nonnegative("Cannot be negative").nullable(),
		contractorType: pelContractorTypeSchema.nullable(),
		region: pelRegionSchema.nullable(),
	})
	.refine(
		({ bondAmount, totalExposure }) =>
			bondAmount === null || totalExposure === null || bondAmount <= totalExposure,
		{ path: ["bondAmount"], error: "Cannot exceed total exposure" },
	);

export const PelPmlCalculatorTab = () => {
	const formMethods = useForm<PelPmlFormValues>({
		defaultValues: {
			totalExposure: null,
			bondAmount: null,
			contractorType: null,
			region: null,
		},
		resolver: createFormResolver<PelPmlFormValues>(formSchema),
		mode: "onChange",
	});
	const { control } = formMethods;

	const totalExposure = useWatch({ control, name: "totalExposure" });
	const bondAmount = useWatch({ control, name: "bondAmount" });
	const contractorType = useWatch({ control, name: "contractorType" });
	const region = useWatch({ control, name: "region" });

	const input = useMemo(() => {
		if (totalExposure === null || totalExposure <= 0) return null;
		if (bondAmount === null || bondAmount < 0 || bondAmount > totalExposure) return null;
		if (contractorType === null || region === null) return null;

		return {
			totalExposure: Currency.round(totalExposure),
			bondAmount: Currency.round(bondAmount),
			contractorType,
			region,
		};
	}, [totalExposure, bondAmount, contractorType, region]);

	const [debouncedInput] = useDebounce(input, 300);
	const calculation = useCalculatePelPml(debouncedInput);

	// Placeholder data describes the previous form and must not appear beside changed inputs.
	const estimate =
		input !== null && debouncedInput !== null && !calculation.isPlaceholderData
			? calculation.data
			: undefined;

	return (
		<div className="grid h-full grid-cols-1 gap-6 overflow-auto pb-2 lg:grid-cols-2">
			<Card elevation="raised" className="shadow-gray-900/10">
				<Card.Header>
					<Card.Title>Exposure</Card.Title>
				</Card.Header>
				<Card.Body className="flex flex-col gap-5 p-6">
					<Text type="body" color="secondary">
						Estimates probable loss severity for a contract surety bonding line using the SFAA 2020
						Construction Loss Severity model.
					</Text>
					<FormCurrencyComponent
						formMethods={formMethods}
						name="bondAmount"
						label="Bond Amount"
						testId="pel-pml-bond-amount"
						required
					/>
					<FormCurrencyComponent
						formMethods={formMethods}
						name="totalExposure"
						label="Total Exposure"
						testId="pel-pml-total-exposure"
						required
					/>
					<FormDropdownComponent
						formMethods={formMethods}
						name="contractorType"
						label="Type of Contractor"
						options={PelContractorType.options()}
						placeholder="Select a contractor type"
						testId="pel-pml-contractor-type"
						required
					/>
					<FormDropdownComponent
						formMethods={formMethods}
						name="region"
						label="Region of Operation"
						options={PelRegion.options()}
						placeholder="Select a region"
						testId="pel-pml-region"
						required
					/>
				</Card.Body>
			</Card>

			<Card elevation="raised" className="shadow-gray-900/10">
				<Card.Header>
					<Card.Title>Loss severity</Card.Title>
				</Card.Header>
				<Card.Body className="flex min-h-[260px] flex-col p-6">
					{calculation.isError ? (
						<Banner tone="danger">
							<Banner.Leading>
								<Icon type="exclamation-circle" size="small" color="danger" />
							</Banner.Leading>
							<Banner.Content>
								<Text type="body" color="danger">
									The calculation could not be completed.
								</Text>
							</Banner.Content>
						</Banner>
					) : estimate !== undefined ? (
						<PelPmlBreakdown result={estimate} showFormulas />
					) : calculation.isFetching ? (
						<div className="flex flex-1 items-center justify-center">
							<Spinner />
						</div>
					) : (
						<EmptyState
							testId="pel-pml-empty-state"
							message="Enter both limits and select a contractor type and region to see the estimate."
						/>
					)}
				</Card.Body>
			</Card>
		</div>
	);
};
